import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useActiveBrand } from "../hooks/useActiveBrand";
import { useTaskCatalog } from "../hooks/useTaskCatalog";
import { OperatorBrandGate } from "../components/layout/OperatorBrandGate";
import { AgentUIComposer } from "../components/agent-v2/AgentUIComposer";
import { WorkspaceContentFrame } from "../components/workspace/WorkspaceContentFrame";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { buildBrandArtifactPath, buildBrandScopedPath } from "../lib/brand-navigation";
import { parseTaskFilters, TASK_CATEGORY_LABELS } from "../lib/task-filters";
import { timeAgo } from "../lib/utils";
import type { TaskCatalogItem, TaskRunStateProjection } from "../../../shared/types";

function recommendedTask(taskCatalog: TaskCatalogItem[]): TaskCatalogItem | null {
  if (taskCatalog.length === 0) return null;

  const neverRun = taskCatalog.find((task) => !task.last_run);
  if (neverRun) return neverRun;

  return [...taskCatalog].sort((left, right) => {
    const leftStartedAt = left.last_run ? new Date(left.last_run.started_at).getTime() : Number.POSITIVE_INFINITY;
    const rightStartedAt = right.last_run ? new Date(right.last_run.started_at).getTime() : Number.POSITIVE_INFINITY;
    return leftStartedAt - rightStartedAt;
  })[0] ?? null;
}

function recommendedTaskReason(task: TaskCatalogItem | null): string {
  if (!task) return "No Action matches the current filters.";
  if (!task.last_run) return "No previous Run is recorded for this Action, so it is the best first pass.";
  return `This Action has the oldest last Run (${timeAgo(task.last_run.started_at)}), so it is the best refresh candidate.`;
}

function taskStateDetail(projection: TaskRunStateProjection | null | undefined): string | null {
  if (!projection) return null;
  const details = [
    projection.workflow_status ? `workflow ${projection.workflow_status}` : null,
    projection.current_node_id ? `stage ${projection.current_node_id}` : null,
    projection.no_op_reason ? `reason ${projection.no_op_reason.replace(/_/g, " ")}` : null,
    projection.prerequisite ? `prerequisite ${projection.prerequisite.replace(/_/g, " ")}` : null,
  ].filter(Boolean);
  return details.length > 0 ? details.join(" · ") : null;
}

// Recurring runs are saved Actions with their own Schedule; the catalog itself
// offers no schedule control (standalone schedules are retired and never fire).
function scheduleSummary(task: TaskCatalogItem): string {
  if (!task.scheduled_run_supported) return "Manual only";
  return "Save this Action to run it on a schedule";
}

function taskRowTiming(task: TaskCatalogItem): string | null {
  return task.last_run ? `Last run ${timeAgo(task.last_run.started_at)}` : null;
}

function isBrandContextSkipped(task: TaskCatalogItem): boolean {
  return task.last_run?.status === "skipped" && task.last_run?.skip_reason === "brand_context_missing";
}

function launchBlocker(task: TaskCatalogItem | null | undefined): string | null {
  return task?.action.prerequisites.find((prerequisite) => !prerequisite.ready)?.explanation ?? null;
}

function isResearchTask(task: TaskCatalogItem | null | undefined): boolean {
  return task?.slug === "market_research";
}

export function TasksPage() {
  const { activeBrand, activeBrandId: brandId } = useActiveBrand();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { category: categoryFilter } = parseTaskFilters(searchParams.toString());
  const detailRef = useRef<HTMLElement>(null);
  function showTaskDetails(slug: string) {
    setSelectedSlug(slug);
    requestAnimationFrame(() => {
      detailRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
      detailRef.current?.focus({ preventScroll: true });
    });
  }
  const [selectedSlug, setSelectedSlug] = useState<string | null>(()=>searchParams.get("action"));
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const { data: taskCatalogRes, isLoading, error } = useTaskCatalog(brandId);
  const tasks = taskCatalogRes?.data ?? [];

  useEffect(() => {
    document.title = "Actions — HelloConvo";
  }, []);

  const visibleTasks = useMemo(() => {
    let next = tasks;
    if (categoryFilter !== "all") {
      next = next.filter((task) => task.category === categoryFilter);
    }
    return next;
  }, [tasks, categoryFilter]);
  const recommendedVisibleTask = useMemo(() => recommendedTask(visibleTasks), [visibleTasks]);

  useEffect(() => {
    if (visibleTasks.length === 0) {
      setSelectedSlug(null);
      return;
    }
    if (!selectedSlug || !visibleTasks.some((task) => task.slug === selectedSlug)) {
      setSelectedSlug(recommendedVisibleTask?.slug ?? visibleTasks[0]?.slug ?? null);
    }
  }, [recommendedVisibleTask?.slug, selectedSlug, visibleTasks]);

  const selectedTask = visibleTasks.find((task) => task.slug === selectedSlug) ?? visibleTasks[0] ?? tasks[0] ?? null;
  function handleRunTask(task: TaskCatalogItem) {
    setSelectedSlug(task.slug);
    navigate(buildBrandScopedPath(`/tasks/start/${encodeURIComponent(task.slug)}`, brandId));
  }

  if (!brandId) {
    return (
      <OperatorBrandGate
        title="Choose a brand before running an action"
        description="Actions and schedules apply to one brand at a time. Select the intended brand before running or scheduling work."
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
        {error instanceof Error ? error.message : "Failed to load Actions"}
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <WorkspaceContentFrame>
        <div className="mx-auto w-full max-w-3xl px-6 py-12">
          <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">Actions</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900">No runnable Actions yet</h1>
          <p className="mt-3 text-slate-600">Complete brand setup so HelloConvo can safely run Actions with the right voice and context.</p>
          <Link className="mt-6 inline-flex rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white" to={buildBrandArtifactPath(brandId, "voice")}>Complete brand setup</Link>
        </div>
      </WorkspaceContentFrame>
    );
  }

  return (
    <WorkspaceContentFrame
      mobileInspectorLabel="Recent activity"
      inspectorOpen={inspectorOpen}
      inspector={
        <aside className="hidden w-[280px] shrink-0 border-l border-slate-200 bg-slate-50/70 xl:flex xl:flex-col">
          <div className="border-b border-slate-200 px-4 py-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">Recent activity</p>
            <h3 className="mt-1 text-lg font-semibold text-slate-900">Last Run status</h3>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4">
            <div className="space-y-3">
              {tasks
                .filter((task) => task.last_run)
                .slice(0, 6)
                .map((task) => (
                  <div key={task.slug} className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium text-slate-900">{task.action.presentation.title}</span>
                      <span className="font-mono text-[10px] text-slate-400">{task.last_run ? timeAgo(task.last_run.started_at) : null}</span>
                    </div>
                    <p className="mt-1 text-sm text-slate-500">
                      {task.last_run?.state_projection?.summary ?? `Status: ${task.last_run?.status ?? "unknown"}`}
                    </p>
                  </div>
                ))}
            </div>
          </div>
        </aside>
      }
    >
      <div className="mx-auto flex w-full max-w-[1120px] flex-1 flex-col px-6 py-8">
        <header className="flex flex-wrap items-start justify-between gap-6">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">Actions</p>
            <h1 className="mt-1 text-[32px] font-bold tracking-tight text-slate-900">
              Run an action{activeBrand?.name ? ` · ${activeBrand.name}` : ""}
            </h1>
            <p className="mt-2 max-w-2xl text-[15px] leading-7 text-slate-500">
              Every Action listed here runs for this brand today. Pick one, or start with the recommendation.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link className="inline-flex min-h-11 items-center rounded-lg border border-slate-300 px-4 text-sm font-medium" to={`/brand/${encodeURIComponent(activeBrand!.id)}/actions`}>Saved Actions</Link>
            <Button variant="secondary" onClick={() => setInspectorOpen((open) => !open)}>
              {inspectorOpen ? "Hide recent activity" : "Show recent activity"}
            </Button>
          </div>
        </header>

        <div className="mt-8 rounded-3xl border border-brand-200 bg-brand-50/60 px-5 py-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-brand-700">Recommended Action</p>
              <h2 className="mt-1 text-lg font-semibold text-slate-900">
                {recommendedVisibleTask?.action.presentation.title ?? "No Actions available"}
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                {recommendedTaskReason(recommendedVisibleTask)}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {recommendedVisibleTask?.last_run ? <Badge variant="info">{timeAgo(recommendedVisibleTask.last_run.started_at)}</Badge> : null}
              {recommendedVisibleTask?.last_run?.findings_count ? (
                <Badge variant="success">
                  {recommendedVisibleTask.last_run.findings_count} finding{recommendedVisibleTask.last_run.findings_count === 1 ? "" : "s"}
                </Badge>
              ) : null}
            </div>
          </div>
          {recommendedVisibleTask ? (
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Button onClick={() => handleRunTask(recommendedVisibleTask)}>
                Run action
              </Button>
              <Button variant="secondary" onClick={() => showTaskDetails(recommendedVisibleTask.slug)}>
                See details
              </Button>
            </div>
          ) : null}
        </div>

        <div className="mt-6 grid items-start gap-6 xl:grid-cols-[minmax(320px,0.78fr)_minmax(560px,1.22fr)]">
          <section className="min-w-0 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">More Actions</p>
                <h2 className="mt-1 text-xl font-semibold text-slate-900">
                  Action library
                </h2>
              </div>
              {categoryFilter !== "all" ? <Badge>{TASK_CATEGORY_LABELS[categoryFilter]}</Badge> : null}
            </div>

            <div className="space-y-3">
              {visibleTasks.length > 0 ? visibleTasks.map((task) => (
                <button
                  key={task.slug}
                  type="button"
                  aria-label={`Open ${task.action.presentation.title} details`}
                  onClick={() => showTaskDetails(task.slug)}
                  className={`w-full rounded-2xl border px-4 py-4 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-200 focus-visible:ring-offset-2 ${
                    selectedTask?.slug === task.slug ? "border-brand-200 bg-brand-50/50" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{task.action.presentation.title}</p>
                      <p className="mt-1 line-clamp-2 text-sm leading-6 text-slate-600">{task.action.presentation.description}</p>
                      {brandId && isBrandContextSkipped(task) ? (
                        <p className="mt-2 text-xs text-amber-700">
                          Skipped — <Link to={buildBrandArtifactPath(brandId, "voice")} className="underline">set up brand voice</Link> to enable this Action.
                        </p>
                      ) : null}
                      {taskRowTiming(task) ? (
                        <p className="mt-2 text-xs text-slate-500">{taskRowTiming(task)}</p>
                      ) : null}
                    </div>
                    <span className="shrink-0 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
                      Details
                    </span>
                  </div>
                </button>
              )) : (
                <p className="text-sm text-slate-500">No Actions in this category yet.</p>
              )}
            </div>
          </section>

          <section ref={detailRef} id="action-details" tabIndex={-1} aria-label="Action details" className="min-w-0 scroll-mt-24 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            {selectedTask ? (
              <>
                <div className="mb-5">
                  <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">Action</p>
                  <h2 className="mt-1 text-xl font-semibold text-slate-900">{selectedTask.action.presentation.title}</h2>
                </div>

                <div className="space-y-4">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <Badge>{TASK_CATEGORY_LABELS[selectedTask.category]}</Badge>
                    </div>
                    <p className="text-sm leading-6 text-slate-600">{selectedTask.action.presentation.description}</p>
                    {brandId && isBrandContextSkipped(selectedTask) ? (
                      <p className="mt-3 text-sm text-amber-800">
                        Skipped — <Link to={buildBrandArtifactPath(brandId, "voice")} className="underline">set up brand voice</Link> to enable this Action.
                      </p>
                    ) : null}
                  </div>

                  {selectedTask.action.launch ? (
                    <div className="rounded-2xl border border-brand-200 bg-brand-50 px-4 py-4">
                      <p className="text-sm font-semibold text-slate-900">{selectedTask.action.launch.title}</p>
                      <p className="mt-1 text-sm leading-6 text-slate-700">{selectedTask.action.launch.description}</p>
                      <Button className="mt-4" disabled={Boolean(launchBlocker(selectedTask))} onClick={() => navigate(buildBrandScopedPath(`/tasks/start/${encodeURIComponent(selectedTask.slug)}`, brandId))}>
                        {selectedTask.action.launch.submit_label}
                      </Button>
                      {launchBlocker(selectedTask) ? <p className="mt-2 text-sm text-amber-800">{launchBlocker(selectedTask)}</p> : null}
                    </div>
                  ) : null}

                  {!isResearchTask(selectedTask) && <div className="grid gap-4 lg:grid-cols-2">
                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
                      <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">Schedule</p>
                      <p className="mt-2 text-sm text-slate-900">{scheduleSummary(selectedTask)}</p>
                      {selectedTask.scheduled_run_supported ? (
                        <Link className="mt-1 inline-block text-sm text-brand-700 underline" to={`/brand/${encodeURIComponent(brandId!)}/actions`}>Saved Actions</Link>
                      ) : null}
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
                      <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">Last outcome</p>
                      {selectedTask.last_run ? (
                        <>
                          <p className="mt-2 text-sm text-slate-900">
                            {selectedTask.last_run.state_projection?.summary ?? `Status: ${selectedTask.last_run.status}`}
                          </p>
                          {taskStateDetail(selectedTask.last_run.state_projection) ? (
                            <p className="mt-1 text-sm text-slate-500">{taskStateDetail(selectedTask.last_run.state_projection)}</p>
                          ) : null}
                          <p className="mt-1 text-sm text-slate-500">
                            {selectedTask.last_run.findings_count} finding{selectedTask.last_run.findings_count === 1 ? "" : "s"}
                          </p>
                        </>
                      ) : (
                        <p className="mt-2 text-sm text-slate-500">No runs recorded yet.</p>
                      )}
                    </div>
                  </div>}

                  <div className="flex flex-wrap gap-3">
                    <Button onClick={() => handleRunTask(selectedTask)}>
                      Run action
                    </Button>
                    {launchBlocker(selectedTask) ? <p className="w-full text-sm text-amber-800">{launchBlocker(selectedTask)}</p> : null}
                    {selectedTask.slug === "market_research" ? (
                      <Link
                        to={`/brand/${encodeURIComponent(brandId)}/research`}
                        className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                      >
                        Open research archive
                      </Link>
                    ) : null}
                    <Link
                      to={buildBrandScopedPath("/runs", brandId)}
                      className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                      View Runs
                    </Link>
                  </div>
                </div>
              </>
            ) : (
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">Action details</p>
                <p className="mt-2 text-sm text-slate-500">Select an Action to inspect its details.</p>
              </div>
            )}
          </section>
        </div>

        <div className="border-t border-transparent pt-4">
          <AgentUIComposer
            onSend={(content) => {
              if (!brandId) return;
              navigate(`/agent/${brandId}`, { state: { draft: content } });
            }}
            onStop={() => {}}
            isStreaming={false}
            disabled={!brandId}
            initialValue=""
            contextLabel="Ask HelloConvo which Action to run and why"
          />
        </div>
      </div>
    </WorkspaceContentFrame>
  );
}
